/**
 * Admin — B2B Leads Pipeline.
 *
 * Liste les leads captés depuis /discovery/demo avec gestion de statut.
 * Transforme le mini-CRM en pipeline commercial activable.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Loader2, CheckCircle2, Mail, RefreshCw, Search,
  MessageSquare, TrendingUp, Download,
} from 'lucide-react';
import { getApiUrl } from '../../config/environment';
import { useAuth } from '../../contexts/AuthContext';

const API = getApiUrl();

const authHeaders = () => {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const STATUS_CONFIG = {
  new:            { label: 'Nouveau',       color: '#1E4F8C', bg: '#E8EEF6' },
  contacted:      { label: 'Contacté',      color: '#92710A', bg: '#FBF9F5' },
  in_discussion:  { label: 'En discussion', color: '#D4A017', bg: '#FFF8E1' },
  closed_won:     { label: 'Gagné',         color: '#2D8659', bg: '#E8F5EC' },
  closed_lost:    { label: 'Perdu',         color: '#C0392B', bg: '#FCE8E6' },
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.new;
  return (
    <span
      data-testid={`lead-status-badge-${status}`}
      className="inline-block px-2.5 py-0.5 text-[10px] tracking-widest uppercase font-semibold rounded-sm"
      style={{ color: cfg.color, backgroundColor: cfg.bg }}>
      {cfg.label}
    </span>
  );
};

export default function AdminB2BLeads() {
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [updating, setUpdating] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/discovery/admin/b2b-leads`, { headers: authHeaders() });
      setLeads(r.data.leads || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Chargement impossible');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const updateStatus = async (email, newStatus) => {
    setUpdating({ ...updating, [email]: true });
    try {
      await axios.patch(`${API}/discovery/admin/b2b-leads/${encodeURIComponent(email)}/status`,
        { status: newStatus },
        { headers: authHeaders() });
      toast.success(`Lead marqué ${STATUS_CONFIG[newStatus].label.toLowerCase()}`);
      // Optimistic update
      setLeads(leads.map(l => l.email === email
        ? { ...l, status: newStatus, last_interaction_at: new Date().toISOString(),
            interactions_count: (l.interactions_count || 0) + 1 }
        : l));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Mise à jour échouée');
    } finally {
      setUpdating({ ...updating, [email]: false });
    }
  };

  const filtered = useMemo(() => {
    return leads.filter(l => {
      if (filterStatus !== 'all' && (l.status || 'new') !== filterStatus) return false;
      if (search) {
        const q = search.toLowerCase();
        return (l.email || '').toLowerCase().includes(q)
            || (l.company || '').toLowerCase().includes(q)
            || (l.role || '').toLowerCase().includes(q);
      }
      return true;
    });
  }, [leads, filterStatus, search]);

  const stats = useMemo(() => {
    const by = { new: 0, contacted: 0, in_discussion: 0, closed_won: 0, closed_lost: 0 };
    leads.forEach(l => {
      const s = l.status || 'new';
      by[s] = (by[s] || 0) + 1;
    });
    return by;
  }, [leads]);

  // KPIs enrichis : total contacté, taux de conversion, pipeline actif
  const kpis = useMemo(() => {
    const total = leads.length;
    const contacted = stats.contacted + stats.in_discussion + stats.closed_won + stats.closed_lost;
    const closed = stats.closed_won + stats.closed_lost;
    const conversionRate = closed > 0 ? Math.round((stats.closed_won / closed) * 100) : 0;
    const activePipeline = stats.new + stats.contacted + stats.in_discussion;
    const contactRate = total > 0 ? Math.round((contacted / total) * 100) : 0;
    return { total, contacted, closed, conversionRate, activePipeline, contactRate };
  }, [leads, stats]);

  const handleExportCsv = async () => {
    try {
      const r = await axios.get(`${API}/discovery/admin/b2b-leads/export.csv`, {
        headers: authHeaders(),
        responseType: 'blob',
      });
      const blob = new Blob([r.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      link.href = url;
      link.setAttribute('download', `b2b_leads_${stamp}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success(`Export CSV : ${leads.length} lead(s)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Export impossible');
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-stone-50" data-testid="admin-b2b-leads-page">
      <div className="bg-[#FBF9F3] text-[#2A2319] py-5 border-b border-[#E8E2D2]">
        <div className="max-w-6xl mx-auto px-6 flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#92710A]">Admin · B2B</div>
            <h1 className="font-serif text-2xl">Pipeline des leads capturés</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleExportCsv} disabled={loading || !leads.length}
                    className="border-[#92710A] text-[#92710A] hover:bg-[#FAF7EE] rounded-sm"
                    data-testid="admin-b2b-leads-export-csv">
              <Download className="w-4 h-4" />
              <span className="ml-2 hidden sm:inline">Exporter CSV</span>
            </Button>
            <Button variant="outline" onClick={load} disabled={loading}
                    className="border-[#92710A] text-[#92710A] hover:bg-[#FAF7EE] rounded-sm"
                    data-testid="admin-b2b-leads-refresh">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              <span className="ml-2 hidden sm:inline">Actualiser</span>
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        {/* KPI row enrichi */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="admin-b2b-kpis">
          <div className="p-4 bg-white border border-stone-200 rounded-sm" data-testid="kpi-total-leads">
            <div className="text-[10px] tracking-widest uppercase text-stone-500">Leads totaux</div>
            <div className="font-serif text-3xl text-[#0F0F10] mt-1">{kpis.total}</div>
          </div>
          <div className="p-4 bg-white border border-stone-200 rounded-sm" data-testid="kpi-active-pipeline">
            <div className="text-[10px] tracking-widest uppercase text-[#92710A]">Pipeline actif</div>
            <div className="font-serif text-3xl text-[#0F0F10] mt-1">{kpis.activePipeline}</div>
            <div className="text-[10px] text-stone-500 mt-0.5">new + contacté + discussion</div>
          </div>
          <div className="p-4 bg-white border border-stone-200 rounded-sm" data-testid="kpi-contact-rate">
            <div className="text-[10px] tracking-widest uppercase text-[#1E4F8C]">Taux de contact</div>
            <div className="font-serif text-3xl text-[#0F0F10] mt-1">{kpis.contactRate}%</div>
            <div className="text-[10px] text-stone-500 mt-0.5">{kpis.contacted}/{kpis.total || 0} leads</div>
          </div>
          <div className="p-4 bg-white border border-stone-200 rounded-sm" data-testid="kpi-conversion-rate">
            <div className="text-[10px] tracking-widest uppercase text-[#2D8659]">Taux de conversion</div>
            <div className="font-serif text-3xl text-[#0F0F10] mt-1">{kpis.conversionRate}%</div>
            <div className="text-[10px] text-stone-500 mt-0.5">{stats.closed_won}/{kpis.closed || 0} clôturés</div>
          </div>
        </div>

        {/* Stats row (filtres par statut) */}
        <div className="grid md:grid-cols-5 gap-3" data-testid="admin-b2b-stats">
          {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
            <button
              key={key}
              onClick={() => setFilterStatus(filterStatus === key ? 'all' : key)}
              data-testid={`admin-b2b-filter-${key}`}
              className={`p-4 border rounded-sm text-left transition-all ${
                filterStatus === key ? 'border-[#0F0F10] shadow-md' : 'border-stone-200 hover:border-stone-400 bg-white'
              }`}>
              <div className="text-[10px] tracking-widest uppercase" style={{ color: cfg.color }}>
                {cfg.label}
              </div>
              <div className="font-serif text-3xl text-[#0F0F10] mt-1">{stats[key] || 0}</div>
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher email, entreprise ou rôle..."
            data-testid="admin-b2b-search"
            className="w-full pl-10 pr-4 py-2.5 border border-stone-300 rounded-sm focus:border-[#92710A] focus:outline-none bg-white"
          />
        </div>

        {/* Leads list */}
        {loading && !leads.length ? (
          <div className="flex items-center gap-2 text-stone-500 py-16 justify-center">
            <Loader2 className="w-5 h-5 animate-spin" /> Chargement...
          </div>
        ) : filtered.length === 0 ? (
          <Card className="p-10 text-center border-dashed" data-testid="admin-b2b-empty">
            <Mail className="w-10 h-10 mx-auto text-stone-300 mb-3" />
            <p className="text-stone-600">
              {search || filterStatus !== 'all'
                ? 'Aucun lead ne correspond au filtre.'
                : "Aucun lead B2B capturé pour l'instant."}
            </p>
          </Card>
        ) : (
          <div className="space-y-2" data-testid="admin-b2b-leads-list">
            {filtered.map(lead => {
              const currentStatus = lead.status || 'new';
              return (
                <Card key={lead.email} className="p-4 flex flex-wrap items-center gap-4"
                      data-testid={`lead-row-${lead.email}`}>
                  <div className="flex-1 min-w-[220px]">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-stone-900">{lead.email}</span>
                      <StatusBadge status={currentStatus} />
                    </div>
                    <div className="text-xs text-stone-500 flex flex-wrap gap-3">
                      {lead.company && <span>🏢 {lead.company}</span>}
                      {lead.role && <span>👤 {lead.role}</span>}
                      {lead.team_size && <span>👥 {lead.team_size}</span>}
                      {lead.archetype_viewed && <span>🎯 {lead.archetype_viewed}</span>}
                    </div>
                    <div className="text-[10px] text-stone-400 mt-1 flex gap-3">
                      <span>Créé: {lead.created_at ? new Date(lead.created_at).toLocaleDateString('fr-FR') : '—'}</span>
                      <span>Dernière interaction: {lead.last_interaction_at ? new Date(lead.last_interaction_at).toLocaleDateString('fr-FR') : '—'}</span>
                      <span>{lead.interactions_count || 1} interaction(s)</span>
                    </div>
                  </div>

                  <div className="flex gap-2 items-center">
                    {currentStatus === 'new' && (
                      <Button
                        size="sm"
                        onClick={() => updateStatus(lead.email, 'contacted')}
                        disabled={updating[lead.email]}
                        data-testid={`lead-mark-contacted-${lead.email}`}
                        className="bg-[#92710A] hover:bg-[#7a5d08] text-white rounded-sm">
                        {updating[lead.email]
                          ? <Loader2 className="w-3 h-3 animate-spin" />
                          : <CheckCircle2 className="w-3 h-3 mr-1" />}
                        Marquer contacté
                      </Button>
                    )}
                    {currentStatus === 'contacted' && (
                      <Button
                        size="sm"
                        onClick={() => updateStatus(lead.email, 'in_discussion')}
                        disabled={updating[lead.email]}
                        data-testid={`lead-mark-discussion-${lead.email}`}
                        variant="outline"
                        className="border-[#D4A017] text-[#92710A] hover:bg-[#FFF8E1] rounded-sm">
                        <MessageSquare className="w-3 h-3 mr-1" /> En discussion
                      </Button>
                    )}
                    {currentStatus === 'in_discussion' && (
                      <>
                        <Button
                          size="sm"
                          onClick={() => updateStatus(lead.email, 'closed_won')}
                          disabled={updating[lead.email]}
                          data-testid={`lead-mark-won-${lead.email}`}
                          className="bg-[#2D8659] hover:bg-[#246B47] text-white rounded-sm">
                          <TrendingUp className="w-3 h-3 mr-1" /> Gagné
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => updateStatus(lead.email, 'closed_lost')}
                          disabled={updating[lead.email]}
                          data-testid={`lead-mark-lost-${lead.email}`}
                          variant="outline"
                          className="border-stone-300 text-stone-600 rounded-sm">
                          Perdu
                        </Button>
                      </>
                    )}
                    <a href={`mailto:${lead.email}`} data-testid={`lead-mailto-${lead.email}`}>
                      <Button size="sm" variant="outline" className="rounded-sm border-stone-300">
                        <Mail className="w-3 h-3" />
                      </Button>
                    </a>
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        <div className="text-center text-xs text-stone-400 pt-2">
          {filtered.length} lead(s) affiché(s) · {leads.length} total
        </div>
      </div>
    </div>
  );
}
